import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { BusinessSettingsLayout } from '@/components/business-settings/BusinessSettingsLayout';
import { 
  CompensationRuleCard,
  CompensationRuleDialog,
  RuleAssignmentDialog,
  CompensationRuleDeletionDialog,
  EmployeeCompensationDialog,
  CompensationPreviewDialog,
  AssignmentHistoryDialog,
} from '@/components/compensation';
import { 
  CompensationChangeApproval,
  ValidationRulesList,
  ApprovalWorkflowCard 
} from '@/components/payroll';
import { ServiceChargeManager } from '@/components/serviceCharge';

import { useCompanyCompensationRules, useCompensationMutations, useCompensationAssignments } from '@/hooks/compensation';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useAuth } from '@/hooks/useAuth';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { 
  Plus, 
  Search, 
  Users, 
  Calculator, 
  Filter,
  Settings,
  DollarSign,
  Clock,
  TrendingUp,
  AlertTriangle,
  UserCheck,
  History
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import type { CompensationRule } from '@/services/compensation/types';

// Assignment Summary Component
function AssignmentSummary() {
  const { data: allRules } = useCompanyCompensationRules();
  const { data: employees } = useCompanyEmployees();
  
  // Calculate assignment statistics
  const activeRules = allRules?.filter(rule => rule.is_active) || [];
  const templateRules = activeRules.length;
  const totalEmployees = employees?.length || 0;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <DollarSign className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Rules</p>
              <p className="text-2xl font-bold">{templateRules}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Employees</p>
              <p className="text-2xl font-bold">{totalEmployees}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Assignment Model</p>
              <p className="text-lg font-semibold">Template-Based</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PaySettings() {
  const [activeTab, setActiveTab] = useState<string>('base-pay');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<CompensationRule | undefined>();
  const [ruleToDelete, setRuleToDelete] = useState<CompensationRule | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [initialRuleType, setInitialRuleType] = useState<string | undefined>(undefined);

  const { user } = useAuth();
  const { data: employees } = useCompanyEmployees();
  const { data: allRules, isLoading: rulesLoading } = useCompanyCompensationRules();
  const { settings: companySettings, updateSettings: updateCompanySettings } = useCompanySettings();
  const { 
    deactivateCompensationRule, 
    deleteCompensationRule, 
    reactivateCompensationRule 
  } = useCompensationMutations();

  // Filter rules based on active tab
  const getRulesForTab = (tabValue: string) => {
    if (!allRules) return [];

    const typeFilters = {
      'base-pay': ['hourly_rate', 'salary'],
      'overtime': ['overtime'],
      'allowances': ['allowance', 'bonus'],
      'deductions': ['deduction']
    };

    return allRules.filter(rule => {
      const matchesType = typeFilters[tabValue as keyof typeof typeFilters]?.includes(rule.rule_type);
      const matchesSearch = !searchQuery || 
        rule.rule_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rule.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && rule.is_active) ||
        (statusFilter === 'inactive' && !rule.is_active);
      
      return matchesType && matchesSearch && matchesStatus;
    });
  };

  const handleCreateRule = (ruleType: string) => {
    setEditingRule(undefined);
    setInitialRuleType(ruleType);
    setShowRuleDialog(true);
  };

  const handleEditRule = (rule: CompensationRule) => {
    setEditingRule(rule);
    setShowRuleDialog(true);
  };

  const handleDeactivateRule = async (ruleId: string) => {
    try {
      await deactivateCompensationRule.mutateAsync(ruleId);
      toast({
        title: "Rule Deactivated",
        description: "Compensation rule has been deactivated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to deactivate compensation rule.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteRule = (ruleId: string) => {
    const rule = allRules?.find(r => r.id === ruleId);
    if (rule) {
      setRuleToDelete(rule);
      setShowDeletionDialog(true);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (ruleToDelete) {
      await handleDeactivateRule(ruleToDelete.id);
      setRuleToDelete(null);
      setShowDeletionDialog(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (ruleToDelete) {
      try {
        await deleteCompensationRule.mutateAsync(ruleToDelete.id);
        toast({
          title: "Rule Deleted",
          description: "Compensation rule has been permanently deleted.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete compensation rule.",
          variant: "destructive",
        });
      }
      setRuleToDelete(null);
      setShowDeletionDialog(false);
    }
  };

  const handleReactivateRule = async (ruleId: string) => {
    try {
      await reactivateCompensationRule.mutateAsync(ruleId);
      toast({
        title: "Rule Reactivated",
        description: "Compensation rule has been reactivated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reactivate compensation rule.",
        variant: "destructive",
      });
    }
  };


  const handleViewEmployees = (rule: CompensationRule) => {
    // Show assignment dialog for the selected rule
    setEditingRule(rule);
    setShowAssignmentDialog(true);
  };

  const getTabIcon = (tabValue: string) => {
    const icons = {
      'base-pay': DollarSign,
      'overtime': Clock,
      'allowances': TrendingUp,
      'deductions': AlertTriangle
    };
    const Icon = icons[tabValue as keyof typeof icons];
    return Icon ? <Icon className="h-4 w-4 mr-2" /> : null;
  };

  const getTabCount = (tabValue: string) => {
    return getRulesForTab(tabValue).length;
  };

  const filteredRules = getRulesForTab(activeTab);

  return (
    <>
      <BusinessSettingsLayout
        title="Compensation Management"
        description="Configure payroll rules, compensation structures, and payment processing settings"
      >
        <div className="space-y-6">
          {/* Assignment Summary */}
          <AssignmentSummary />
          
          {/* Header Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-1 items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search compensation rules..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <SelectTrigger className="w-32">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEmployeeDialog(true)}
              >
                <Users className="h-4 w-4 mr-2" />
                Manage Employees
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowPreviewDialog(true)}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Preview Payroll
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowHistoryDialog(true)}
              >
                <History className="h-4 w-4 mr-2" />
                View History
              </Button>
              
              <Button onClick={() => handleCreateRule('hourly_rate')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </div>
          </div>

          {/* Enhanced Approval Integration */}
          {showApprovalDialog && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Compensation Change Management
                </CardTitle>
                <CardDescription>
                  Review pending changes, validate rules, and track approval workflows
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <CompensationChangeApproval 
                    pendingChanges={[]}
                    onApprove={async () => {}}
                    onReject={async () => {}}
                    onBulkAction={async () => {}}
                    companyId={user?.id || ''}
                  />
                  <Separator />
                  <ValidationRulesList 
                    isLoading={false}
                    selectedChanges={[]}
                  />
                  <Separator />
                  <ApprovalWorkflowCard 
                    change={{
                      id: 'sample-1',
                      type: 'create',
                      status: 'pending',
                      priority: 'medium',
                      rule: {
                        id: 'rule-1',
                        company_id: 'sample-company-id',
                        rule_name: 'Sample Hourly Rate',
                        rule_type: 'hourly_rate',
                        description: 'Sample rule for demonstration',
                        calculation_method: 'fixed_amount',
                        base_rate: 25.00,
                        percentage_rate: 0,
                        multiplier_rate: 1.0,
                        threshold_hours: 0,
                        threshold_amount: 0,
                        priority_order: 1,
                        applies_to_holidays: false,
                        applies_to_overtime: false,
                        is_active: true,
                        effective_from: new Date().toISOString().split('T')[0],
                        effective_to: null,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                        created_by: user?.id || null,
                        tiered_rates: [],
                        overtime_trigger: 'daily_hours'
                      } as any,
                      submittedBy: {
                        id: user?.id || '',
                        name: 'Current User',
                        role: 'admin'
                      },
                      submittedAt: new Date().toISOString(),
                      impactEstimate: {
                        total_cost_change: 1000,
                        affected_employee_count: 1,
                        estimated_gross_change: 1000,
                        comparison_period: 'monthly'
                      }
                    }}
                    isSelected={false}
                    onSelect={() => {}}
                    onApprove={() => {}}
                    onReject={() => {}}
                    onView={() => {}}
                    isLoading={false}
                   />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compensation Rules Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="base-pay" className="flex items-center gap-2">
                {getTabIcon('base-pay')}
                Base Pay
                <Badge variant="secondary" className="ml-2">
                  {getTabCount('base-pay')}
                </Badge>
              </TabsTrigger>
              
              <TabsTrigger value="overtime" className="flex items-center gap-2">
                {getTabIcon('overtime')}
                Overtime
                <Badge variant="secondary" className="ml-2">
                  {getTabCount('overtime')}
                </Badge>
              </TabsTrigger>
              
              <TabsTrigger value="allowances" className="flex items-center gap-2">
                {getTabIcon('allowances')}
                Allowances
                <Badge variant="secondary" className="ml-2">
                  {getTabCount('allowances')}
                </Badge>
              </TabsTrigger>
              
              <TabsTrigger value="deductions" className="flex items-center gap-2">
                {getTabIcon('deductions')}
                Deductions
                <Badge variant="secondary" className="ml-2">
                  {getTabCount('deductions')}
                </Badge>
              </TabsTrigger>
              
              <TabsTrigger value="service-charge" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Service Charge
              </TabsTrigger>
            </TabsList>

            {/* Base Pay Tab */}
            <TabsContent value="base-pay" className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Base Pay Rules</h3>
                <p className="text-sm text-muted-foreground">
                  Configure hourly rates and salary structures for employees
                </p>
              </div>

              {rulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredRules.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredRules.map((rule) => (
                     <CompensationRuleCard
                       key={rule.id}
                       rule={rule}
                       onEdit={handleEditRule}
                       onDeactivate={handleDeactivateRule}
                       onDelete={handleDeleteRule}
                       onReactivate={handleReactivateRule}
                       onViewEmployees={handleViewEmployees}
                     />
                  ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <DollarSign className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Base Pay Rules</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Get started by creating hourly rate or salary rules for your employees
                    </p>
                    <Button onClick={() => handleCreateRule('hourly_rate')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Rule
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Overtime Tab */}
            <TabsContent value="overtime" className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Overtime Rules</h3>
                <p className="text-sm text-muted-foreground">
                  Configure overtime thresholds and multiplier rates
                </p>
              </div>

              {rulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredRules.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                   {filteredRules.map((rule) => (
                     <CompensationRuleCard
                       key={rule.id}
                       rule={rule}
                       onEdit={handleEditRule}
                       onDeactivate={handleDeactivateRule}
                       onDelete={handleDeleteRule}
                       onReactivate={handleReactivateRule}
                       onViewEmployees={handleViewEmployees}
                     />
                   ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Overtime Rules</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Set up overtime rules to handle extended work hours with appropriate rates
                    </p>
                    <Button onClick={() => handleCreateRule('overtime')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Overtime Rule
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Allowances Tab */}
            <TabsContent value="allowances" className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Allowances & Bonuses</h3>
                <p className="text-sm text-muted-foreground">
                  Configure additional compensation like allowances and bonuses
                </p>
              </div>

              {rulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredRules.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                   {filteredRules.map((rule) => (
                     <CompensationRuleCard
                       key={rule.id}
                       rule={rule}
                       onEdit={handleEditRule}
                       onDeactivate={handleDeactivateRule}
                       onDelete={handleDeleteRule}
                       onReactivate={handleReactivateRule}
                       onViewEmployees={handleViewEmployees}
                     />
                   ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Allowances or Bonuses</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Add allowances and bonuses to enhance employee compensation
                    </p>
                    <Button onClick={() => handleCreateRule('allowance')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Allowance
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Deductions Tab */}
            <TabsContent value="deductions" className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Deductions</h3>
                <p className="text-sm text-muted-foreground">
                  Configure deductions for taxes, benefits, or other reductions
                </p>
              </div>

              {rulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredRules.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                   {filteredRules.map((rule) => (
                     <CompensationRuleCard
                       key={rule.id}
                       rule={rule}
                       onEdit={handleEditRule}
                       onDeactivate={handleDeactivateRule}
                       onDelete={handleDeleteRule}
                       onReactivate={handleReactivateRule}
                       onViewEmployees={handleViewEmployees}
                     />
                   ))}
                </div>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Deductions</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Configure deductions for taxes, insurance, or other payroll reductions
                    </p>
                    <Button onClick={() => handleCreateRule('deduction')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Deduction
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Service Charge Tab */}
            <TabsContent value="service-charge" className="space-y-4">
              <ServiceChargeManager />
            </TabsContent>
            
            
          </Tabs>
        </div>
      </BusinessSettingsLayout>

      {/* Dialogs */}
      {showRuleDialog && (
        <CompensationRuleDialog
          open={showRuleDialog}
          onOpenChange={(open) => {
            setShowRuleDialog(open);
            if (!open) setInitialRuleType(undefined);
          }}
          rule={editingRule}
          mode={editingRule ? 'edit' : 'create'}
          initialRuleType={initialRuleType}
          onRuleCreated={(newRule) => {
            // PHASE 3: After rule creation, show assignment dialog
            setEditingRule(newRule);
            setShowAssignmentDialog(true);
            setShowRuleDialog(false);
            setInitialRuleType(undefined);
          }}
        />
      )}

      <EmployeeCompensationDialog
        open={showEmployeeDialog}
        onOpenChange={setShowEmployeeDialog}
        selectedEmployeeId=""
      />

      {/* PHASE 6: Assignment History Dialog */}
      <AssignmentHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
      />

      {/* PHASE 3: Rule Assignment Dialog */}
      <RuleAssignmentDialog
        open={showAssignmentDialog}
        onOpenChange={setShowAssignmentDialog}
        rule={editingRule || null}
      />

      <CompensationPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
      />

      {/* Deletion Dialog */}
      <CompensationRuleDeletionDialog
        rule={ruleToDelete}
        open={showDeletionDialog}
        onOpenChange={(open) => {
          setShowDeletionDialog(open);
          if (!open) setRuleToDelete(null);
        }}
        onConfirmDeactivate={handleConfirmDeactivate}
        onConfirmDelete={handleConfirmDelete}
      />

      {/* Close approval dialog when clicking outside */}
      {showApprovalDialog && (
        <div 
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setShowApprovalDialog(false)}
        />
      )}
    </>
  );
}